import { useStore } from '@tanstack/react-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormTextField } from '@/components/ui/form-text-field';
import { Input } from '@/components/ui/input';
import { FieldError } from '@/components/ui/field-error';
import { FormCombobox } from '@/components/ui/form-combobox';
import { eventTypeOptions, eventFormClasses } from '@/lib/utils/events.utils';
import type { EventFormInstance } from '@/lib/hooks/useEventForm';
import { EditEventContent } from '@/content/EditEventContent';
import { RecurrenceSelector } from './RecurrenceSelector';
import { FormDateField } from '../ui/form-date-field';
import { EventDescriptionField } from './EventDescriptionField';

type EventDetailsSectionProps = {
  form: EventFormInstance;
  submitAttempted?: boolean;
  validationErrors?: Record<string, string[]>;
  isEditMode?: boolean;
};

/**
 * Event details section of the event form
 */
export function EventDetailsSection({
  form,
  submitAttempted = false,
  validationErrors = {},
  isEditMode = false,
}: EventDetailsSectionProps) {
  // Subscribe to form values needed for recurrence preview
  const { eventDate, startTime } = useStore(form.store, (state) => ({
    eventDate: state.values.eventDate,
    startTime: state.values.startTime,
  }));

  return (
    <Card>
      <CardHeader className="space-y-2">
        <CardTitle>Event details</CardTitle>
        <div className="h-[1px] bg-[#F9F8F8]/15 border-0" />
      </CardHeader>

      <CardContent className="space-y-6">
        <form.Field name="title">
          {(field) => (
            <FormTextField
              field={field}
              label="Title"
              placeholder="Event title"
              autoComplete="off"
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.title}
            />
          )}
        </form.Field>

        <form.Field name="description">
          {(field) => (
            <EventDescriptionField
              field={field}
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.description}
            />
          )}
        </form.Field>

        <form.Field name="location">
          {(field) => (
            <FormTextField
              field={field}
              label="Location"
              placeholder="Founder's Hub, Seattle"
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.location}
            />
          )}
        </form.Field>

        <form.Field name="linkName">
          {(field) => (
            <FormTextField
              field={field}
              label={EditEventContent.linkName}
              placeholder={EditEventContent.linkNamePlaceholder}
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.linkName}
            />
          )}
        </form.Field>
        <form.Field name="eventLink">
          {(field) => (
            <FormTextField
              field={field}
              label={EditEventContent.eventLink}
              placeholder={EditEventContent.eventLinkPlaceholder}
              type="url"
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.eventLink}
            />
          )}
        </form.Field>

        <form.Field name="eventDate">
          {(field) => (
            <FormDateField
              field={field}
              label="Event Date"
              forceShowErrors={submitAttempted}
              externalErrors={validationErrors.eventDate}
            />
          )}
        </form.Field>

        <div className="space-y-2">
          <label className="text-large text-slate-50 font-semibold">Time</label>
          <div className="grid grid-cols-2 gap-6">
            <form.Field name="startTime">
              {(field) => {
                const errors =
                  validationErrors.startTime || field.state.meta.errors || [];
                const hasErrors = errors.length > 0;
                const showErrors =
                  submitAttempted || field.state.meta.isTouched;
                const isInvalid = showErrors && hasErrors;
                return (
                  <div className="space-y-2">
                    <Input
                      id={field.name}
                      name={field.name}
                      type="time"
                      value={field.state.value ?? ''}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      onBlur={field.handleBlur}
                      aria-invalid={isInvalid || undefined}
                      className={`${eventFormClasses.input} ${isInvalid ? 'border-red-500' : ''}`}
                    />
                    <label
                      className="text-body text-slate-50"
                      htmlFor={field.name}
                    >
                      Start time
                    </label>
                    {showErrors && <FieldError errors={errors} />}
                  </div>
                );
              }}
            </form.Field>

            <form.Field name="endTime">
              {(field) => {
                const errors =
                  validationErrors.endTime || field.state.meta.errors || [];
                const hasErrors = errors.length > 0;
                const showErrors =
                  submitAttempted || field.state.meta.isTouched;
                const isInvalid = showErrors && hasErrors;
                return (
                  <div className="space-y-2">
                    <Input
                      id={field.name}
                      name={field.name}
                      type="time"
                      value={field.state.value ?? ''}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      onBlur={field.handleBlur}
                      aria-invalid={isInvalid || undefined}
                      className={`${eventFormClasses.input} ${isInvalid ? 'border-red-500' : ''}`}
                    />
                    <label
                      className="text-body text-slate-50"
                      htmlFor={field.name}
                    >
                      End time
                    </label>
                    {showErrors && <FieldError errors={errors} />}
                  </div>
                );
              }}
            </form.Field>
          </div>
        </div>

        <form.Field name="eventType">
          {(field) => (
            <FormCombobox
              id={field.name}
              name={field.name}
              label="Event type"
              value={field.state.value}
              options={eventTypeOptions}
              placeholder="Select or type event type"
              helperText="This is for internal purposes only and will not be displayed to users."
              errorText={validationErrors.eventType || field.state.meta.errors}
              forceShowErrors={submitAttempted}
              isTouched={field.state.meta.isTouched}
              onChange={(value) => field.handleChange(value)}
              onBlur={field.handleBlur}
            />
          )}
        </form.Field>

        {/* Recurrence selector */}
        <div className="space-y-4">
          <form.Field name="recurrenceType">
            {(recurrenceTypeField) => (
              <form.Field name="recurrenceCount">
                {(recurrenceCountField) => {
                  const recurrenceTypeErrors =
                    validationErrors.recurrenceType ||
                    recurrenceTypeField.state.meta.errors ||
                    [];
                  const recurrenceCountErrors =
                    validationErrors.recurrenceCount ||
                    recurrenceCountField.state.meta.errors ||
                    [];
                  const showErrors =
                    submitAttempted ||
                    recurrenceTypeField.state.meta.isTouched ||
                    recurrenceCountField.state.meta.isTouched;

                  return (
                    <RecurrenceSelector
                      disabled={isEditMode}
                      recurrenceType={recurrenceTypeField.state.value}
                      recurrenceCount={recurrenceCountField.state.value ?? 2}
                      eventDate={eventDate ?? ''}
                      startTime={startTime ?? ''}
                      onRecurrenceTypeChange={(value) =>
                        recurrenceTypeField.handleChange(value)
                      }
                      onRecurrenceCountChange={(value) =>
                        recurrenceCountField.handleChange(value)
                      }
                      onRecurrenceTypeBlur={recurrenceTypeField.handleBlur}
                      onRecurrenceCountBlur={recurrenceCountField.handleBlur}
                      recurrenceTypeErrors={recurrenceTypeErrors}
                      recurrenceCountErrors={recurrenceCountErrors}
                      showErrors={showErrors}
                    />
                  );
                }}
              </form.Field>
            )}
          </form.Field>
        </div>
      </CardContent>
    </Card>
  );
}
